// components/AuthPage/ForgotPasswordForm.js
// Three-step forgot password flow: email → OTP → new password
import { useState, useEffect, useRef, useCallback } from "react";
import { forgotPassword, resetPassword } from "../../APIs/authApi";
import FloatingInput from "./FloatingInput";
import { AuthButton } from "./LoginForm";

const RESEND_SECONDS = 60;

/* ── Step 1: Enter email ────────────────────────────── */
function EmailStep({ onSent }) {
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await forgotPassword(email.trim().toLowerCase());
      onSent(email.trim().toLowerCase());
    } catch (err) {
      const d = err.response?.data;
      setError(d?.error || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="animate-[fin_.3s_cubic-bezier(.16,1,.3,1)_both]">
      <div className="mb-5 text-center">
        <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-3"
             style={{ background: "rgba(79,142,247,0.12)", border: "1px solid rgba(79,142,247,0.2)" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4f8ef7" strokeWidth="1.8">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <p className="text-[13px] text-slate-400 leading-relaxed">
          Enter your email and we'll send<br/>a 6-digit reset code.
        </p>
      </div>

      {error && (
        <div className="mb-3 rounded-[9px] border border-red-500/20 bg-red-500/[.07] px-3 py-2.5 text-[12.5px] text-red-300/90 leading-[1.5]">
          {error}
        </div>
      )}

      <FloatingInput
        name="email" label="Email address" type="email"
        value={email} onChange={(e) => setEmail(e.target.value)}
        required autoComplete="email" className="mb-3.5"
      />

      <AuthButton loading={loading} label="Send Reset Code" loadingLabel="Sending…" />
    </form>
  );
}

/* ── Step 2: OTP + new password ─────────────────────── */
function OtpStep({ email, onSuccess }) {
  const [digits,   setDigits]   = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [timer,    setTimer]    = useState(RESEND_SECONDS);
  const [resending, setResending] = useState(false);
  const [otpDone,  setOtpDone]  = useState(false);
  const inputRefs = useRef([]);

  // Countdown
  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  const handleDigitChange = (index, value) => {
    if (value.length > 1) {
      const pasted = value.replace(/\D/g, "").slice(0, 6);
      const next = ["", "", "", "", "", ""];
      for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
      setDigits(next);
      const focusAt = Math.min(pasted.length, 5);
      inputRefs.current[focusAt]?.focus();
      if (pasted.length === 6) setOtpDone(true);
      return;
    }
    const digit = value.replace(/\D/g, "");
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (next.every((d) => d !== "")) setOtpDone(true);
    else setOtpDone(false);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = useCallback(async () => {
    const code = digits.join("");
    if (code.length < 6) { setError("Enter all 6 digits."); return; }
    if (!password)       { setError("Enter your new password."); return; }
    setLoading(true); setError(null);
    try {
      await resetPassword(email, code, password);
      onSuccess();
    } catch (err) {
      const d = err.response?.data;
      setError(d?.error || "Verification failed. Please try again.");
      setDigits(["", "", "", "", "", ""]);
      setOtpDone(false);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }, [digits, password, email, onSuccess]);

  const handleResend = async () => {
    if (timer > 0) return;
    setResending(true); setError(null);
    try {
      await forgotPassword(email);
      setTimer(RESEND_SECONDS);
      setDigits(["", "", "", "", "", ""]);
      setOtpDone(false);
      inputRefs.current[0]?.focus();
    } catch {
      setError("Could not resend code. Try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="animate-[fin_.3s_cubic-bezier(.16,1,.3,1)_both]">
      <div className="mb-5 text-center">
        <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-3"
             style={{ background: "rgba(79,142,247,0.12)", border: "1px solid rgba(79,142,247,0.2)" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4f8ef7" strokeWidth="1.8">
            <rect x="2" y="4" width="20" height="16" rx="2"/>
            <path d="M22 7l-10 7L2 7"/>
          </svg>
        </div>
        <p className="text-[13px] text-slate-400 leading-relaxed">
          Code sent to <span className="text-slate-200 font-medium">{email}</span>
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-[9px] border border-red-500/20 bg-red-500/[.07] px-3 py-2.5 text-[12.5px] text-red-300/90 leading-[1.5]">
          {error}
        </div>
      )}

      {/* 6 digit boxes */}
      <div className="flex gap-2 justify-center mb-4">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={d}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={loading}
            className={[
              "w-10 h-12 text-center text-[20px] font-bold rounded-xl outline-none transition-all duration-150",
              "bg-white/[0.04] border text-white caret-blue-400",
              d ? "border-blue-500/50 bg-blue-500/[0.07]"
                : "border-white/[0.08] focus:border-blue-500/40 focus:bg-white/[0.06]",
              loading ? "opacity-50 cursor-not-allowed" : "",
            ].join(" ")}
          />
        ))}
      </div>

      {/* New password */}
      <FloatingInput
        name="new_password" label="New password" type="password"
        value={password} onChange={(e) => setPassword(e.target.value)}
        required autoComplete="new-password" className="mb-4"
      />

      <button
        onClick={handleSubmit}
        disabled={loading || !otpDone || !password}
        className={[
          "w-full py-[13px] rounded-xl text-[13.5px] font-semibold tracking-[.04em] transition-all duration-200",
          loading || !otpDone || !password
            ? "bg-blue-600/30 text-blue-400/50 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_20px_rgba(37,99,235,0.35)]",
        ].join(" ")}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity=".25"/>
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            </svg>
            Resetting…
          </span>
        ) : "Reset Password"}
      </button>

      <p className="mt-3 text-center text-[12px] text-slate-600">
        Didn't receive it?{" "}
        {timer > 0 ? (
          <span className="text-slate-500">Resend in {timer}s</span>
        ) : (
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-blue-500 hover:text-blue-400 transition-colors disabled:opacity-50"
          >
            {resending ? "Sending…" : "Resend code"}
          </button>
        )}
      </p>
    </div>
  );
}

/* ── Step 3: Success ────────────────────────────────── */
function SuccessStep({ onGoToLogin }) {
  return (
    <div className="animate-[fin_.3s_cubic-bezier(.16,1,.3,1)_both] text-center py-4">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-4"
           style={{ background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.2)" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>
      <p className="text-[15px] font-semibold text-white mb-1">Password reset!</p>
      <p className="text-[13px] text-slate-400 mb-6">You can now sign in with your new password.</p>
      <button
        onClick={onGoToLogin}
        className="w-full py-[13px] rounded-xl text-[13.5px] font-semibold bg-blue-600 hover:bg-blue-500 text-white transition-all"
      >
        Go to Sign In
      </button>
    </div>
  );
}

/* ── Main export ────────────────────────────────────── */
export default function ForgotPasswordForm({ onGoToLogin }) {
  const [step,  setStep]  = useState("email"); // email | otp | done
  const [email, setEmail] = useState("");

  if (step === "done")  return <SuccessStep onGoToLogin={onGoToLogin} />;
  if (step === "otp")   return <OtpStep email={email} onSuccess={() => setStep("done")} />;

  return (
    <EmailStep
      onSent={(e) => { setEmail(e); setStep("otp"); }}
    />
  );
}
